<template>
  <v-dialog 
    :value="isOpen"
    persistent
    scrollable
    content-class="add-edit-dialog">
    <v-card>
      <v-toolbar class="escalon-dialog-header" height="35">
        <img
          class="modal-axis-logo mr-2" 
          :src="require('../../assets/' + coloredLogo)" />
        <span>{{modalTitle}}</span>
        <v-spacer/>
        <close-button @click="closeModal"/>
      </v-toolbar>
      <v-card-text>
        <v-form v-model="valid" @submit.prevent="submit">
          <h2 class="add-edit-modal-header secondary">Patient Information</h2>
          <v-container fluid grid-list-md>
            <v-layout wrap>
              <v-flex md4>
                <div>
                  <label class="modal-label">Patient Id*</label>
                </div>
                <v-text-field
                  solo
                  v-model="patient.ptId"
                  name="patient.ptId"
                  v-validate="'required|max:50'"
                  data-vv-as="Patient Id"
                  :error-messages="errors.collect('patient.ptId')"
                  :disabled="editPatientDisabled"/>
              </v-flex>
              <v-flex md4>
                <div>
                  <label class="modal-label">Patient Last Name*</label>
                </div>
                <v-text-field
                  solo
                  v-model="patient.lastName"
                  name="patient.lastName"
                  v-validate="'required|max:256'"
                  data-vv-as="Patient Last Name"
                  :error-messages="errors.collect('patient.lastName')"
                  :disabled="editPatientDisabled"/>
              </v-flex>
              <v-flex md4>
                <div>
                  <label class="modal-label">Patient First Name*</label>
                </div>
                <v-text-field
                  solo
                  v-model="patient.firstName"
                  name="patient.firstName"
                  v-validate="'required|max:256'"
                  data-vv-as="Patient First Name"
                  :error-messages="errors.collect('patient.firstName')"
                  :disabled="editPatientDisabled"/>
              </v-flex>
              <v-flex md4>
                <div>
                  <label class="modal-label">Patient Gender</label>
                </div>
                <select-with-auto-complete-field
                  v-model="patient.gender"
                  name="patient.gender"
                  :items="genders"
                  data-vv-as="Patient Gender"
                  :error-messages="errors.collect('patient.gender')"
                  :disabled="editPatientDisabled"/>
              </v-flex>
              <v-flex md4>
                <div>
                  <label class="modal-label">Patient Date Of Birth*</label>
                </div>
                <date-menu
                  :value="patient.dob"
                  @change="setDOB"
                  name="patient.dob"
                  v-validate="'required|date|inPast'"
                  data-vv-as="Patient Date Of Birth"
                  :error-messages="errors.collect('patient.dob')"
                  :disabled="editPatientDisabled"/>
              </v-flex>
              <v-flex md4 v-if="isEditing || isManualUpload">
                <div>
                  <label class="modal-label">Patient Status*</label>
                </div>
                <select-with-auto-complete-field
                  v-model="patient.status"
                  :items="patientStatus"
                  name="patient.status"
                  v-validate="'required'"
                  data-vv-as="Patient Status"
                  :error-messages="errors.collect('patient.status')"
                  :disabled="editPatientDisabled"/>
              </v-flex>
            </v-layout>
          </v-container>
          <h2 class="add-edit-modal-header" v-if="isAddingExam">Exam Information</h2>
          <v-container fluid grid-list-md v-if="isAddingExam">
            <v-layout wrap>
              <v-flex md4>
                <div>
                  <label class="modal-label">Exam Date*</label>
                </div>
                <date-menu
                  :value="patient.exam.date"
                  @change="setExamDate"
                  name="patient.exam.date"
                  v-validate="'required|date'"
                  data-vv-as="Exam Date"
                  :error-messages="errors.collect('patient.exam.date')"
                  :disabled="editExamDisabled"/>
              </v-flex>
              <v-flex md4>
                <div>
                  <label class="modal-label">Exam Physician</label>
                </div>
                <select-with-auto-complete-field
                  v-model="patient.exam.physician"
                  :items="physicians"
                  item-value="phySrNo"
                  item-text="name"
                  name="patient.exam.physician"
                  v-validate="''"
                  data-vv-as="Exam Physician"
                  :error-messages="errors.collect('patient.exam.physician')"
                  :disabled="editExamDisabled"/>
              </v-flex>
              <v-flex md4>
                <div>
                  <label class="modal-label">Exam Type*</label>
                </div>
                <select-with-auto-complete-field
                  v-model="patient.exam.type"
                  :items="devices"
                  item-value="devSrNo"
                  item-text="procedure"
                  name="patient.exam.type"
                  v-validate="'required'"
                  data-vv-as="Exam Type"
                  :error-messages="errors.collect('patient.exam.type')"
                  :disabled="editExamDisabled"/>
              </v-flex>
            </v-layout>
          </v-container>
          <v-toolbar v-if="isAddingExam" height="55">
            <div class="dragAndDrop" 
              v-cloak 
              @drop.prevent="addFile" 
              @dragover.prevent>
              <span>Drop files here!</span>
            </div>
            <v-spacer/>
            <v-btn text
              @click="uploadFile"
              class="modal-cta"
              :disabled="editExamDisabled">
              Upload
              <v-icon right>fas fa-cloud-upload-alt</v-icon>
            </v-btn>
            <v-btn text
              @click="clearFiles"
              class="modal-cta"
              :disabled="editExamDisabled">
              Clear
              <v-icon right>fas fa-undo</v-icon>
            </v-btn>
          </v-toolbar>
          <v-container fluid v-if="isAddingExam">
            <patient-file-upload-table
              :files="patient.files"
              @deleteFile="deleteFile"
              :disabled="editExamDisabled"/>
          </v-container>
          <input
            type="file"
            multiple
            v-show="false"
            ref="fileInput"
            @change="onFileSelected"
            :disabled="editExamDisabled">
          <patient-merge-info v-if="isMergingPatient" :patients="patientsToMerge"/>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <span
          v-if="isAddingExam"
          class="files-selected-text"
          :class="colorClass">Total Files Selected: {{patient.files.length}}</span>
        <v-spacer/>
        <div v-if="isAddingExam" class="modal-progress-bar-wrapper">
          <v-progress-linear
            v-model="uploadProgress"
            height="20"
            color="#3f3f3f"/>
          <span
            class="progress-bar-text"
            :class="colorClass">{{uploadCurrentFormatted}} of {{uploadGoalFormatted}} Uploaded</span>
        </div>
        <v-spacer/>
        <v-btn text
          @click="submit"
          class="modal-cta"
          :disabled="submitDisabled"
          v-if="!isMergingPatient">
          Submit
          <v-avatar size="20" class="ml-2">
            <img :src="require('../../assets/' + coloredAssetUrls.submit)">
          </v-avatar>
        </v-btn>
        <v-btn
          text
          class="modal-cta"
          v-if="isMergingPatient"
          @click="closeModal"
          :disabled="submitDisabled">Don't Merge</v-btn>
        <v-btn
          text
          class="modal-cta"
          v-if="isMergingPatient"
          @click="$emit('merge')"
          :disabled="submitDisabled">Merge</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { GENDERS, EYES, PATIENT_STATUS } from "../../config/config";
import { formatBytes } from "../../helpers/utils";
import dateMenu from "../controls/DateMenu";
import selectWithAutoCompleteField from "../controls/SelectWithAutoCompleteField";
import closeButton from "../controls/CloseButton";
import patientFileUploadTable from "./PatientFileUploadTable";
import patientMergeInfo from "./PatientMergeInfo";

export default {
  name: "addEditPatientDialog",
  components: {
    dateMenu,
    selectWithAutoCompleteField,
    patientFileUploadTable,
    patientMergeInfo,
    closeButton
  },
  props: {
    patient: Object,
    isEditing: Boolean,
    isAdding: Boolean,
    isManualUpload: Boolean,
    isAddingExam: Boolean,
    isSaving: Boolean,
    isMergingPatient: Boolean,
    canEditPatient: Boolean,
    patientsToMerge: Array
  },
  data() {
    return {
      genders: GENDERS,
      eyes: EYES,
      patientStatus: PATIENT_STATUS,
      valid: false
    };
  },
  computed: {
    physicians() {
      let activePhysicians = [...this.$store.getters.physicians].filter(e => {
          return e.phyStatus == 1;
      });
      return activePhysicians.sort((a, b) => a.lastName.localeCompare(b.lastName));
    },
    devices() {
      return this.$store.getters.devices;
    },
    uploadCurrent() {
      if (!this.patient.files) return 0;
      return this.aggregateBytes(this.patient.files, file => file.uploaded);
    },
    uploadCurrentFormatted() {
      return formatBytes(this.uploadCurrent);
    },
    uploadGoal() {
      if (!this.patient.files) return 0;
      return this.aggregateBytes(this.patient.files, file => file.size);
    },
    uploadGoalFormatted() {
      return formatBytes(this.uploadGoal);
    },
    uploadProgress() {
      if (!this.uploadCurrent || !this.uploadGoal) return 0;
      return (this.uploadCurrent / this.uploadGoal) * 100;
    },
    modalTitle() {
      if (this.isManualUpload)
        return "Manual Upload";
      return (this.isAdding ? "Add New" : "Edit") + " Patient And Exam";
    },
    isOpen() {
      return this.isAdding || this.isEditing || this.isManualUpload;
    },
    editPatientDisabled() {
      return this.isSaving || this.isManualUpload || this.isMergingPatient || !this.canEditPatient;
    },
    editExamDisabled() {
      return this.isSaving || this.isMergingPatient || !this.canEditPatient;
    },
    submitDisabled() {
      return this.isSaving || !this.canEditPatient;
    }
  },
  methods: {
    uploadFile() {
      this.$refs.fileInput.value = null;
      this.$refs.fileInput.click();
    },
    onFileSelected(e) {
      if (e.target.files) {
        for (let i = 0; i < e.target.files.length; i++) {
          var file = e.target.files[i];
          file.state = "Pending";
          file.uploaded = 0;
          file.eye = this.patient.exam.eye;
          file.batchEyeSelected = false;
          this.patient.files.push(file);
        }
      }
    },
    aggregateBytes(files, valueExpression) {
      return files.reduce(
        (total, current) => total + valueExpression(current),
        0
      );
    },
    deleteFile(file) {
      var index = this.patient.files.indexOf(file);
      if (index !== -1) this.$delete(this.patient.files, index);
    },
    clearFiles() {
      this.patient.files = [];
    },
    validateFiles() {
      // todo - any deeper file validation? types? sizes? signatures?
      if (!this.patient.files.length)
        return Promise.reject("Please upload at least one file");
      return Promise.resolve(true);
    },
    submit() {
      this.$validator
        .validateAll()
        .then(valid => {
          if (!valid) return Promise.reject("Please fix all form errors");
          if (this.isAddingExam) return this.validateFiles();
          return Promise.resolve(true);
        })
        .then(() => {
          this.$emit("submit");
        })
        .catch(msg => {
          this.$store.commit("displayStatus", { message: msg, level: "error" });
        });
    },
    closeModal() {
      this.$emit("close");
      this.errors.clear();
    },
    setDOB(value){
      this.$set(this.patient, "dob", value);
    },
    setExamDate(value){
      this.$set(this.patient.exam, "date", value);
    },
    addFile(e) {
      let droppedFiles = e.dataTransfer.files;
      if(!droppedFiles) return;
      ([...droppedFiles]).forEach(f => {
        var file = f;
        file.state = "Pending";
        file.uploaded = 0;
        file.eye = this.patient.exam.eye;
        file.batchEyeSelected = false;
        this.patient.files.push(file);
      });
    }
  }
}
</script>